import { faEye, faPlus, faSearch, faTrash } from "@fortawesome/free-solid-svg-icons"
import { Modal, Pagination, Tab, Tabs, TextInput, Link } from "@wfp/ui"
import Button from "components/Shared/Button/Button"
import PermissionWrapper from "components/Shared/Common/PermissionWrapper"
import DeleteConfirmationModal from "components/Shared/ConfirmationModal/DeleteConfirmationModal"
import CountrySelector from "components/Shared/HeaderFilters/AssociatedCountriesSelector"
import HeaderFilters from "components/Shared/HeaderFilters/HeaderFilters"
import { formatDate } from "utils/helpers"
import { Commodities, IPermission, SearchResponse, Survey, SurveyById } from "interfaces/dataInterfaces"
import { Dispatch, SetStateAction, useRef, useState } from "react"
import BaseTable, { AutoResizer, Column, ColumnShape } from "react-base-table"
import { useNavigate } from "react-router-dom"

interface ISurveyData {
  admCode: number | null
  addAdmCode?: number | null
  xlsFormId?: number | null
  query: string
  pageNumber: number
  itemList: number
  actionItem: { item: Survey; action: "details" | "delete" } | null
}

interface ISurveyPage {
  data: ISurveyData
  setData: Dispatch<SetStateAction<ISurveyData>>
  permission: IPermission
  surveyData?: SearchResponse<Survey>
  surveyById?: SurveyById
  commodityBasket?: any
  deleteSurvey: (id: number) => void
  downloadSurveyFile: any
  downloadOriginSurvey: any
}

export default function SurveyPage({
  data,
  setData,
  permission,
  surveyData,
  surveyById,
  commodityBasket,
  deleteSurvey,
  downloadOriginSurvey,
  downloadSurveyFile,
}: ISurveyPage) {
  const navigate = useNavigate()
  const [isSearchMode, setIsSearchMode] = useState(false)

  const columns: Array<ColumnShape<Survey>> = [
    {
      key: "surveyId",
      dataKey: "surveyId",
      title: "ID",
      width: 150,
      maxWidth: 150,
    },
    {
      key: "countryName",
      dataKey: "countryName",
      title: "Country",
      width: 150,
    },
    {
      key: "xlsFormName",
      dataKey: "xlsFormName",
      title: "XLSForm Name",
      width: 200,
    },
    {
      key: "surveyCreateDate",
      // dataKey: "surveyCreateDate",
      title: "Created Date",
      width: 200,
      cellRenderer: ({ rowData }) => formatDate(rowData.surveyCreateDate),
    },
    {
      key: "userName",
      dataKey: "userName",
      title: "Create By",
      width: 200,
    },
    {
      key: "action",
      width: 250,
      align: Column.Alignment.CENTER,
      flexGrow: 1,
      cellRenderer: ({ rowData }) => (
        <>
          <PermissionWrapper {...permission} feature="details">
            <Button
              action={() =>
                setData({
                  ...data,
                  actionItem: {
                    item: rowData,
                    action: "details",
                  },
                })
              }
              label="Details"
              className="btn-full blue action-btn"
              icon={faEye}
            />
          </PermissionWrapper>
          <PermissionWrapper {...permission} feature="delete">
            <Button
              action={() =>
                setData({
                  ...data,
                  actionItem: {
                    item: rowData,
                    action: "delete",
                  },
                })
              }
              label="Delete"
              className="btn-full danger action-btn"
              style={{ marginLeft: "10px" }}
              icon={faTrash}
            />
          </PermissionWrapper>
        </>
      ),
    },
  ]

  const commoditiesColumns: Array<ColumnShape<Commodities>> = [
    {
      key: "commodityId",
      dataKey: "commodityId",
      title: "CommodityId",
      width: 200,
    },
    {
      key: "commodityUnitId",
      dataKey: "commodityUnitId",
      title: "CommodityUnitId",
      width: 200,
    },
    {
      key: "label",
      dataKey: "label",
      title: "Label",
      width: 300,
    },
  ]

  const inputRef = useRef<TextInput>(null)
  const handleSearch = () => {
    const q = (inputRef.current as any).value as string
    if (!q.length) return
    setData({ ...data, query: q, pageNumber: 1 })
    setIsSearchMode(true)
  }
  const handleClearSearch = () => {
    const input = inputRef.current as any
    input.value = ""
    setData({ ...data, query: "", pageNumber: 1 })
    setIsSearchMode(false)
  }

  const getadata = async (event: any) => {
    setData({ ...data, admCode: event?.value, pageNumber: 1 })
  }

  const handleDownloadSurvey = (surveyId: number | undefined, dataType: string, format = "") => {
    downloadSurveyFile({ surveyId, dataType, format } as any)
  }

  return (
    <>
      <HeaderFilters>
        <div className="d-flex" style={{ width: "80%" }}>
          <CountrySelector onSelect={getadata} />
          <TextInput
            name="query"
            placeholder="Type here to filter results..."
            type="text"
            style={{ fontSize: "0.875rem", borderRadius: 4 }}
            ref={inputRef}
          />
          <Button
            action={handleSearch}
            label="Search"
            className="btn-full blue"
            icon={faSearch}
            style={{ marginLeft: "10px", padding: 12, marginTop: -2 }}
          />
        </div>
        <div className="d-flex" style={{ width: "20%" }}>
          {isSearchMode && (
            <Button
              action={handleClearSearch}
              label="Clear"
              className="btn-full blue"
              style={{ marginLeft: "10px", padding: 12, marginTop: -2 }}
            />
          )}
        </div>
        <PermissionWrapper {...permission} feature="addNew">
          <Button action={() => navigate("add")} label="Add New Survey" className="btn-full green" icon={faPlus} />
        </PermissionWrapper>
      </HeaderFilters>

      <div style={{ height: "100%", minHeight: "60vh", marginTop: "10px" }}>
        <AutoResizer>
          {({ width, height }) => (
            <BaseTable
              data={surveyData?.items}
              width={width}
              height={height}
              rowKey="surveyId"
              className="custom-table"
              rowHeight={38}
              headerHeight={45}
              columns={columns}
              emptyRenderer={() => <p>No records to display</p>}
            />
          )}
        </AutoResizer>
      </div>

      <Modal
        open={data?.actionItem?.action === "details"}
        modalHeading="Survey Infos"
        primaryButtonText="Close"
        onRequestClose={() =>
          setData({
            ...data,
            actionItem: null,
          })
        }
        onRequestSubmit={() =>
          setData({
            ...data,
            actionItem: null,
          })
        }
        wide
      >
        <Tabs>
          <Tab label="Details" href="" role="tab" selected tabIndex={0}>
            <div className="grid grid-cols-5 gap-4">
              <div>
                <p>Create Date:</p>
                <p>Base XLSForm:</p>
                <p>XLSForm:</p>
                <p>Start Date:</p>
                <p>End Date:</p>
                <p>Uploaded by:</p>
                <p>Validation Report:</p>
              </div>

              <div className="col-span-4">
                <p>{formatDate(surveyById?.surveyCreateDate || "", "DD-MMMM-YYYY HH:mm") ?? "-"}</p>
                <p>{surveyById?.baseXlsFormName ?? "-"} </p>
                <p>{surveyById?.xlsFormName ?? "-"} </p>
                <p>{formatDate(surveyById?.surveyStartDate || "") ?? "-"} </p>
                <p>{formatDate(surveyById?.surveyEndDate || "") ?? "-"} </p>
                <p>{surveyById?.userName ?? "-"} </p>
                <p className="fix-box">{surveyById?.surveyValidationReport ?? "-"} </p>
              </div>
            </div>

            {commodityBasket && (
              <div className="grid grid-cols-5 gap-4 mt-3" style={{ minHeight: "30vh" }}>
                <div>
                  <p>Basket Commodity:</p>
                </div>
                <div className="col-span-3">
                  <AutoResizer>
                    {({ width, height }) => (
                      <BaseTable
                        data={commodityBasket}
                        width={width}
                        height={height}
                        rowKey="commodityId"
                        className="custom-table"
                        rowHeight={30}
                        headerHeight={35}
                        columns={commoditiesColumns}
                        emptyRenderer={() => <p>No records to display</p>}
                      />
                    )}
                  </AutoResizer>
                </div>
              </div>
            )}
          </Tab>
          <Tab label="Data Export" href="" role="tab" selected tabIndex={0}>
            <div className="grid grid-cols-5 gap-4">
              <div>
                <p>CSV file:</p>
                <p>Full data JSON:</p>
                <p>Base data JSON:</p>

                <p>Processed Data CSV:</p>
                <p>Processed Data JSON:</p>
              </div>

              <div className="col-span-4">
                <p className="truncate">
                  <Link
                    onClick={() =>
                      downloadOriginSurvey({ surveyId: surveyById?.surveyId, file: surveyById?.surveyOriginalFilename })
                    }
                  >
                    <span>{surveyById?.surveyOriginalFilename ?? "-"}</span>
                  </Link>
                </p>
                <p>
                  <Link onClick={() => handleDownloadSurvey(surveyById?.surveyId, "FullData")}>View</Link>
                </p>
                <p>
                  <Link onClick={() => handleDownloadSurvey(surveyById?.surveyId, "BaseData")}>View</Link>
                </p>

                <p>
                  <Link onClick={() => handleDownloadSurvey(surveyById?.surveyId, "ProcessedData", "csv")}>View</Link>{" "}
                  <span className="text-sm italic">(Processed data is available after 24hrs from the upload)</span>
                </p>
                <p>
                  <Link onClick={() => handleDownloadSurvey(surveyById?.surveyId, "ProcessedData")}>View</Link>{" "}
                  <span className="text-sm italic">(Processed data is available after 24hrs from the upload)</span>
                </p>
              </div>
            </div>
          </Tab>
        </Tabs>
      </Modal>

      <DeleteConfirmationModal
        open={data?.actionItem?.action === "delete"}
        onRequestClose={() =>
          setData({
            ...data,
            actionItem: null,
          })
        }
        onRequestSubmit={() => {
          deleteSurvey(data?.actionItem?.item.surveyId as number)
          setData({
            ...data,
            actionItem: null,
          })
        }}
      />

      <Pagination
        totalItems={surveyData?.count ?? 0}
        pageSize={data.itemList}
        pageSizes={[10, 20, 30]}
        page={data.pageNumber}
        onChange={(event: any) => {
          setData({ ...data, pageNumber: event.page, itemList: event.pageSize })
        }}
      />
    </>
  )
}
